import { IonButton, IonIcon, IonSpinner, useIonAlert } from '@ionic/react';
import React, { useCallback, useRef, useState } from 'react';
import { once, run } from '../../../helper/electron';
import { useIcons } from '../../../hooks/useIcons';
import { useDispatch, useSelector } from '../../../store/hooks';
import { DriveInfo } from '../../../store/slices/drive-state.slice';
import {
  setPreferencesActiveSection,
  setShowPreferences,
} from '../../../store/slices/preferences.slice';
import {
  selectAllTrackMatches,
} from '../../../store/slices/track-matcher.slice';
import { iTrack } from '../../../types/ITrack';
import { copyToClipboard } from '../../../utils/copyToClipboard';
import { exportCsvUtil } from '../../../utils/exportCsv';
import { exportPdfUtil } from '../../../utils/exportPdf';
import isElectron from '../../../utils/isElectron';
import { normalizePath } from '../../../utils/normalizePath';
import {
  FilePathMap,
  getPossibleTrackIds,
  resolveExportTrackPathMap,
} from '../../../utils/resolveExportTrackPathMap';
import { useToast } from '../../ToastManager/useToast';
import { type SeratoExportTarget } from '../../../utils/seratoExportVersion';
import './InlineExportButtons.scss';
import RekordboxGuidePopover from './RekordboxExportPopover';

const COPY_PREF_KEY = 'export_copyExternalPref';
const COPY_DEST_KEY = 'export_copyExternalDest';

export type CopyExternalPref = 'always-copy' | 'never-copy' | null;

export const getCopyExternalPref = (): CopyExternalPref => {
  const val = localStorage.getItem(COPY_PREF_KEY);
  if (val === 'always-copy' || val === 'never-copy') return val;
  return null;
};

export const setCopyExternalPref = (pref: CopyExternalPref): void => {
  if (pref) {
    localStorage.setItem(COPY_PREF_KEY, pref);
  } else {
    localStorage.removeItem(COPY_PREF_KEY);
  }
};

export const getCopyExternalDest = (): string | null =>
  localStorage.getItem(COPY_DEST_KEY);

export const setCopyExternalDest = (dest: string | null): void => {
  if (dest) {
    localStorage.setItem(COPY_DEST_KEY, dest);
  } else {
    localStorage.removeItem(COPY_DEST_KEY);
  }
};

interface ICloudCrateLink {
  name: string;
  link: string;
}

interface InlineExportButtonsProps {
  crateId?: string;
  crateName: string;
  tracks: FilePathMap;
  trackInfo: iTrack[];
  hacker: string;
  cloudCrateList: ICloudCrateLink[];
}

type ExportType =
  | 'serato3'
  | 'serato4'
  | 'rekordbox'
  | 'virtualdj'
  | 'm3u'
  | 'pdf'
  | 'csv';

const SERATO_EXPORT_TARGET: Record<'serato3' | 'serato4', SeratoExportTarget> =
  {
    serato3: 'legacy',
    serato4: 'serato4',
  };

const isSeratoExportType = (
  type: ExportType,
): type is 'serato3' | 'serato4' =>
  type === 'serato3' || type === 'serato4';

const exportSuccessLabel = (type: ExportType): string => {
  if (type === 'serato3') return 'Serato 3';
  if (type === 'serato4') return 'Serato 4+';
  if (type === 'rekordbox') return 'REKORDBOX';
  if (type === 'virtualdj') return 'VIRTUALDJ';
  return type.toUpperCase();
};

const isExternalPath = (
  filePath: string,
  connectedDrives: DriveInfo[],
): boolean => {
  if (filePath.startsWith('/Volumes/')) return true;
  if (filePath.startsWith('\\\\')) return true;

  const winMatch = filePath.match(/^([A-Za-z]):[/\\]/);
  if (winMatch) {
    const driveLetter = winMatch[1].toUpperCase() + ':';
    const driveInfo = connectedDrives.find((d) => {
      const normalizedPath = d.path.replace(/\\/g, '/').toUpperCase();
      return normalizedPath.startsWith(driveLetter);
    });

    if (driveInfo) {
      if (
        driveInfo.isRemovable !== undefined ||
        driveInfo.isNetwork !== undefined
      ) {
        return !!driveInfo.isNetwork || !!driveInfo.isRemovable;
      }
    }
    return winMatch[1].toUpperCase() !== 'C';
  }

  return false;
};

const getFileName = (filePath: string): string => {
  const parts = filePath.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || filePath;
};

export const InlineExportButtons: React.FC<InlineExportButtonsProps> = ({
  crateId,
  crateName,
  tracks,
  trackInfo,
  hacker,
  cloudCrateList,
}) => {
  const dispatch = useDispatch();
  const allTrackMatches = useSelector(selectAllTrackMatches);
  const connectedDrives = useSelector(
    (state: any) => state.driveState?.connectedDrives || [],
  );
  const { icons } = useIcons();
  const { successToast, errorToast } = useToast();
  const [exportingType, setExportingType] = useState<
    ExportType | 'unmatched-csv' | 'unmatched-copy' | null
  >(null);
  const [copyingFiles, setCopyingFiles] = useState(false);
  const [presentAlert] = useIonAlert();
  const pendingExportRef = useRef<{
    type: ExportType;
    filePaths: string[];
    seratoTracks: { filePath: string }[];
    seratoExportTarget?: SeratoExportTarget;
  } | null>(null);
  const [showRekordBoxModal, setShowRekordBoxModal] = useState(false);
  const [rekordBoxPath, setRekordBoxPath] = useState('');
  const getPossibleTrackIdsForTrack = useCallback(
    (track: iTrack): string[] => getPossibleTrackIds(track),
    [],
  );

  // Resolve matched file paths using all known ID formats for a track
  const getTrackPaths = useCallback(
    (track: iTrack, pathMap: FilePathMap): string[] => {
      const possibleIds = getPossibleTrackIdsForTrack(track);
      for (const id of possibleIds) {
        const paths = pathMap[id];
        if (paths && paths.length > 0) {
          return paths.filter((path) =>
            Boolean(path && path.trim().length > 0),
          );
        }
      }

      return [];
    },
    [getPossibleTrackIdsForTrack],
  );

  const resolveTrackPathMap = useCallback(async (): Promise<FilePathMap> => {
    return resolveExportTrackPathMap({
      crateId,
      trackInfo,
      inMemoryPaths: tracks,
      allTrackMatches,
      dispatch,
    });
  }, [tracks, crateId, trackInfo, dispatch, allTrackMatches]);

  // Get file paths for M3U/VirtualDJ export methods
  const getStandardExportPaths = useCallback(
    (pathMap: FilePathMap) => {
      const filePaths: string[] = [];
      trackInfo.forEach((track) => {
        const paths = getTrackPaths(track, pathMap);
        filePaths.push(...paths);
      });
      return filePaths;
    },
    [trackInfo, getTrackPaths],
  );

  // Get Serato payload tracks
  const getSeratoExportTracks = useCallback(
    (pathMap: FilePathMap) => {
      const filePaths: { filePath: string }[] = [];
      trackInfo.forEach((track) => {
        const paths = getTrackPaths(track, pathMap);
        paths.forEach((filePath) => {
          filePaths.push({ filePath });
        });
      });
      return filePaths;
    },
    [trackInfo, getTrackPaths],
  );

  const findExternalPaths = useCallback(
    (filePaths: string[]): string[] =>
      filePaths.filter((p) => isExternalPath(p, connectedDrives)),
    [connectedDrives],
  );

  type ConflictChoice = 'skip' | 'overwrite' | 'cancel';

  const checkFilesExistBatch = useCallback(
    (paths: string[]): Promise<Record<string, boolean>> => {
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.warn(
            '[Export] check-files-exist-batch timed out, assuming no conflicts',
          );
          resolve({});
        }, 5000);

        window.api?.send('check-files-exist-batch', { paths });
        window.api?.once(
          'check-files-exist-batch-response',
          (results: Record<string, boolean>) => {
            clearTimeout(timeout);
            resolve(results ?? {});
          },
        );
      });
    },
    [],
  );

  const promptConflict = useCallback(
    (
      fileName: string,
      remaining: number,
    ): Promise<{ choice: ConflictChoice; applyToAll: boolean }> => {
      return new Promise((resolve) => {
        let result: { choice: ConflictChoice; applyToAll: boolean } = {
          choice: 'cancel',
          applyToAll: false,
        };
        presentAlert({
          header: 'File Already Exists',
          message: `"${fileName}" already exists in the destination folder.${remaining > 0 ? ` (${remaining} more conflict${remaining === 1 ? '' : 's'} after this)` : ''}`,
          inputs:
            remaining > 0
              ? [
                  {
                    type: 'checkbox' as const,
                    label: 'Apply to all remaining conflicts',
                    value: 'applyAll',
                    checked: false,
                  },
                ]
              : [],
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
              handler: (data: string[]) => {
                result = {
                  choice: 'cancel',
                  applyToAll: data?.includes('applyAll') ?? false,
                };
              },
            },
            {
              text: 'Skip',
              handler: (data: string[]) => {
                result = {
                  choice: 'skip',
                  applyToAll: data?.includes('applyAll') ?? false,
                };
              },
            },
            {
              text: 'Overwrite',
              handler: (data: string[]) => {
                result = {
                  choice: 'overwrite',
                  applyToAll: data?.includes('applyAll') ?? false,
                };
              },
            },
          ],
          onDidDismiss: () => {
            resolve(result);
          },
        });
      });
    },
    [presentAlert],
  );

  const doCopyFiles = useCallback(
    (
      items: { current_path: string; new_path: string }[],
      destination: string,
      overwrite: boolean,
    ): Promise<{
      processedFiles: string[];
      failedFiles: string[];
    }> => {
      return new Promise((resolve) => {
        if (items.length === 0) {
          resolve({ processedFiles: [], failedFiles: [] });
          return;
        }
        window.api?.send('file-operation', {
          action: overwrite ? 'copy' : 'non-overwriting-copy',
          destination,
          items,
        });
        window.api?.once('file-operation', (response: any) => {
          const processed: string[] = [];
          const failed: string[] = [];
          if (response?.processedFiles) {
            response.processedFiles.forEach((f: any) => {
              if (f?.new_path) processed.push(f.new_path);
            });
          }
          if (response?.failedFiles) {
            response.failedFiles.forEach((f: any) => {
              failed.push(f?.current_path ?? '');
            });
          }
          resolve({ processedFiles: processed, failedFiles: failed });
        });
      });
    },
    [],
  );

  const copyFilesToLocal = useCallback(
    async (
      externalPaths: string[],
      destination: string,
    ): Promise<{ copiedMap: Map<string, string>; cancelled: boolean }> => {
      const items = externalPaths.map((p) => ({
        source: p,
        dest: `${destination}/${getFileName(p)}`,
      }));

      const existsMap = await checkFilesExistBatch(items.map((i) => i.dest));

      const conflicts = items.filter((i) => existsMap[i.dest]);
      const noConflicts = items.filter((i) => !existsMap[i.dest]);

      const copiedMap = new Map<string, string>();
      const toCopy = noConflicts.map((i) => ({
        current_path: i.source,
        new_path: i.dest,
      }));
      const toOverwrite: { current_path: string; new_path: string }[] = [];

      if (conflicts.length > 0) {
        let bulkChoice: ConflictChoice | null = null;

        for (let i = 0; i < conflicts.length; i++) {
          const item = conflicts[i];
          const remaining = conflicts.length - i - 1;
          let choice: ConflictChoice;

          if (bulkChoice) {
            choice = bulkChoice;
          } else {
            const result = await promptConflict(
              getFileName(item.source),
              remaining,
            );
            choice = result.choice;
            if (result.applyToAll) bulkChoice = choice;
          }

          if (choice === 'cancel') {
            return { copiedMap, cancelled: true };
          } else if (choice === 'skip') {
            copiedMap.set(item.source, item.dest);
          } else if (choice === 'overwrite') {
            toOverwrite.push({
              current_path: item.source,
              new_path: item.dest,
            });
          }
        }
      }

      setCopyingFiles(true);

      if (toCopy.length > 0) {
        const result = await doCopyFiles(toCopy, destination, false);
        toCopy.forEach((item) => {
          if (!result.failedFiles.includes(item.current_path)) {
            copiedMap.set(item.current_path, item.new_path);
          }
        });
      }

      if (toOverwrite.length > 0) {
        const result = await doCopyFiles(toOverwrite, destination, true);
        toOverwrite.forEach((item) => {
          if (!result.failedFiles.includes(item.current_path)) {
            copiedMap.set(item.current_path, item.new_path);
          }
        });
      }

      setCopyingFiles(false);

      const totalCopied = copiedMap.size;
      const skipped = conflicts.length - toOverwrite.length;
      if (totalCopied > 0) {
        const parts = [];
        if (totalCopied - skipped > 0)
          parts.push(`Copied ${totalCopied - skipped}`);
        if (skipped > 0) parts.push(`${skipped} already in place`);
        successToast({ message: parts.join(', ') });
      }

      return { copiedMap, cancelled: false };
    },
    [checkFilesExistBatch, promptConflict, doCopyFiles, successToast],
  );

  const replaceExternalPaths = useCallback(
    (filePaths: string[], copiedMap: Map<string, string>): string[] =>
      filePaths.map((p) => copiedMap.get(p) ?? p),
    [],
  );

  const replaceExternalSeratoPaths = useCallback(
    (
      tracks: { filePath: string }[],
      copiedMap: Map<string, string>,
    ): { filePath: string }[] =>
      tracks.map((t) => ({
        filePath: copiedMap.get(t.filePath) ?? t.filePath,
      })),
    [],
  );

  const pickDestinationAndCopy = useCallback(
    async (
      externalPaths: string[],
    ): Promise<{
      copiedMap: Map<string, string>;
      cancelled: boolean;
    } | null> => {
      return new Promise((resolve) => {
        const savedDest = getCopyExternalDest();

        const doCopy = async (dest: string) => {
          setCopyExternalDest(dest);
          const result = await copyFilesToLocal(externalPaths, dest);
          resolve(result);
        };

        if (savedDest) {
          doCopy(savedDest);
          return;
        }

        run('open-directory-dialog', {
          options: ['openDirectory', 'createDirectory', 'promptToCreate'],
          title: 'Choose a local folder to copy external tracks into',
          buttonLabel: 'Copy Here',
        });

        once('open-directory-dialog', (result: any) => {
          if (result && result.length > 0) {
            doCopy(result[0].path);
          } else {
            resolve(null);
          }
        });
      });
    },
    [copyFilesToLocal],
  );

  const promptForExternalDrives = useCallback(
    (externalPaths: string[], onCopy: () => void, onSkip: () => void) => {
      const driveNames = Array.from(
        new Set(
          externalPaths.map((p) => {
            const volMatch = p.match(/^\/Volumes\/([^/]+)/);
            if (volMatch) return volMatch[1];
            const winMatch = p.match(/^([A-Za-z]):[/\\]/);
            if (winMatch) return `${winMatch[1]}:\\`;
            return 'External Drive';
          }),
        ),
      );

      presentAlert({
        header: 'External Drive Tracks Detected',
        message: `${externalPaths.length} track${externalPaths.length === 1 ? '' : 's'} found on ${driveNames.join(', ')}. Copy to a local folder before exporting?`,
        inputs: [
          {
            type: 'checkbox',
            label: "Don't ask me again",
            value: 'remember',
            checked: false,
          },
        ],
        buttons: [
          {
            text: 'Leave on External Drive',
            role: 'cancel',
            handler: (data: string[]) => {
              if (data?.includes('remember')) {
                setCopyExternalPref('never-copy');
              }
              onSkip();
            },
          },
          {
            text: 'Copy to Local',
            handler: (data: string[]) => {
              if (data?.includes('remember')) {
                setCopyExternalPref('always-copy');
              }
              onCopy();
            },
          },
        ],
      });
    },
    [presentAlert],
  );

  // Warn when tracks live on a disconnected external drive.
  // Resolves true to continue the export, false to cancel.
  const promptDisconnectedDrives = useCallback(
    (driveNames: string[], skippedCount: number): Promise<boolean> => {
      const trackLabel = skippedCount === 1 ? 'track' : 'tracks';
      return new Promise((resolve) => {
        let proceed = false;
        presentAlert({
          header: 'External Drive Not Connected',
          message:
            `${skippedCount} ${trackLabel} in this export ${skippedCount === 1 ? 'is' : 'are'} ` +
            `on an external drive that isn't connected (${driveNames.join(', ')}). ` +
            `${skippedCount === 1 ? 'It' : 'They'} may not carry over to your ` +
            `export. You can plug in the drive and re-export to include ` +
            `${skippedCount === 1 ? 'it' : 'them'}. If this message seems ` +
            `incorrect, please reach out to support.`,
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                proceed = false;
              },
            },
            {
              text: 'Export Anyway',
              handler: () => {
                proceed = true;
              },
            },
          ],
          onDidDismiss: () => {
            resolve(proceed);
          },
        });
      });
    },
    [presentAlert],
  );

  // Execute the export IPC call
  const executeExport = useCallback(
    (
      type: ExportType,
      filePaths: string[],
      path?: string,
      seratoTracks?: { filePath: string }[],
      seratoExportTarget?: SeratoExportTarget,
    ) => {
      setExportingType(type);

      const ipcMethod = isSeratoExportType(type)
        ? 'serato'
        : type === 'rekordbox'
          ? 'm3u'
          : type;
      const resultEventName = `exportCrate:${ipcMethod}`;
      let didReceiveResult = false;

      once(resultEventName, (result: any) => {
        didReceiveResult = true;
        setExportingType(null);
        let parsedResult: any;
        try {
          parsedResult =
            typeof result === 'string' ? JSON.parse(result) : result;
        } catch (parseError) {
          errorToast({
            message: `Export to ${exportSuccessLabel(type)} failed: Invalid response received`,
          });
          return;
        }

        if (parsedResult?.success) {
          successToast({
            message: `Exported to ${exportSuccessLabel(type)} successfully`,
          });
          if (Array.isArray(parsedResult.warnings)) {
            parsedResult.warnings.forEach((warning: string) => {
              errorToast({ message: warning });
            });
          }
        } else {
          const errorMsg =
            parsedResult?.error ||
            parsedResult?.message ||
            `Failed to export to ${exportSuccessLabel(type)}`;

          if (
            errorMsg.includes('folder') ||
            errorMsg.includes('path') ||
            errorMsg.includes('not found')
          ) {
            errorToast({
              message: 'Please configure export folder in Settings',
            });
            dispatch(setPreferencesActiveSection('dj-software'));
            dispatch(setShowPreferences(true));
          } else {
            errorToast({ message: errorMsg });
          }
        }
      });

      const payload: Record<string, any> = {
        method: ipcMethod,
        title: crateName,
        tracks: isSeratoExportType(type) ? (seratoTracks ?? []) : filePaths,
        path,
      };

      if (isSeratoExportType(type)) {
        payload.seratoOptions = {
          exportTarget: seratoExportTarget ?? SERATO_EXPORT_TARGET[type],
        };
      }

      run('exportCrate', payload);

      setTimeout(() => {
        if (!didReceiveResult) {
          setExportingType((current) => (current === type ? null : current));
          errorToast({
            message: `Export to ${exportSuccessLabel(type)} timed out. Check export folder settings and try again.`,
          });
        }
      }, 30000);
    },
    [crateName, successToast, errorToast, dispatch],
  );

  const proceedWithExport = useCallback(
    (
      type: ExportType,
      standardPaths: string[],
      seratoTracks: { filePath: string }[],
      seratoExportTarget?: SeratoExportTarget,
    ) => {
      if (type === 'm3u' || type === 'rekordbox') {
        try {
          run('open-directory-dialog', {
            options: ['openDirectory', 'createDirectory', 'promptToCreate'],
            title: `Save ${type === 'rekordbox' ? 'Rekordbox' : 'M3U'} playlist`,
            buttonLabel: 'Save Here',
          });

          once('open-directory-dialog', (result: any) => {
            if (result && result.length > 0) {
              executeExport(
                type,
                standardPaths,
                result[0].path,
                seratoTracks,
                seratoExportTarget,
              );
              const hideRekordExportGuide =
                JSON.parse(
                  localStorage.getItem('hide-rekord-export-guide') ?? '{}',
                ).hide ?? false;

              if (hideRekordExportGuide == 'false' || !hideRekordExportGuide) {
                setShowRekordBoxModal(true);
                setRekordBoxPath(result[0].path);
              }
            } else {
              setExportingType(null);
            }
          });
        } catch (error) {
          errorToast({ message: 'Failed to open folder picker' });
          setExportingType(null);
        }
        return;
      }

      executeExport(
        type,
        standardPaths,
        undefined,
        seratoTracks,
        seratoExportTarget,
      );
    },
    [executeExport, errorToast],
  );

  const handleDjExport = useCallback(
    async (type: ExportType) => {
      if (!isElectron()) {
        errorToast({
          message: 'DJ exports are only available in the desktop app',
        });
        return;
      }

      setExportingType(type);

      try {
        const resolvedSeratoTarget = isSeratoExportType(type)
          ? SERATO_EXPORT_TARGET[type]
          : undefined;

        const pathMap = await resolveTrackPathMap();
        const standardPaths = getStandardExportPaths(pathMap);
        const seratoTracks = getSeratoExportTracks(pathMap);

        if (standardPaths.length === 0 && seratoTracks.length === 0) {
          errorToast({ message: 'No tracks with file paths to export' });
          setExportingType(null);
          return;
        }

        const externalPaths = findExternalPaths(standardPaths);

        if (externalPaths.length === 0) {
          proceedWithExport(
            type,
            standardPaths,
            seratoTracks,
            resolvedSeratoTarget,
          );
          return;
        }

        const existsMap = await checkFilesExistBatch(externalPaths);
        // Only treat a path as disconnected when the main process explicitly
        // reports it missing. On an IPC timeout the map is empty (undefined
        // values), so we proceed normally instead of falsely blocking exports.
        const disconnectedPaths = externalPaths.filter(
          (p) => existsMap[p] === false,
        );
        if (disconnectedPaths.length > 0) {
          const disconnectedDrives = Array.from(
            new Set(
              disconnectedPaths.map((p) => {
                const volMatch = p.match(/^\/Volumes\/([^/]+)/);
                if (volMatch) return volMatch[1];
                const winMatch = p.match(/^([A-Za-z]):[/\\]/);
                if (winMatch) return `${winMatch[1]}:\\`;
                return 'External Drive';
              }),
            ),
          );
          const proceed = await promptDisconnectedDrives(
            disconnectedDrives,
            disconnectedPaths.length,
          );
          if (!proceed) {
            setExportingType(null);
            return;
          }
        }

        const connectedExternalPaths = externalPaths.filter(
          (p) => existsMap[p] !== false,
        );

        if (connectedExternalPaths.length === 0) {
          proceedWithExport(
            type,
            standardPaths,
            seratoTracks,
            resolvedSeratoTarget,
          );
          return;
        }

        const pref = getCopyExternalPref();

        if (pref === 'never-copy') {
          proceedWithExport(
            type,
            standardPaths,
            seratoTracks,
            resolvedSeratoTarget,
          );
          return;
        }

        const doCopyAndExport = async () => {
          const result = await pickDestinationAndCopy(connectedExternalPaths);
          if (!result || result.cancelled) {
            if (result?.cancelled) {
              setExportingType(null);
              return;
            }
            proceedWithExport(
              type,
              standardPaths,
              seratoTracks,
              resolvedSeratoTarget,
            );
            return;
          }
          const updatedPaths = replaceExternalPaths(
            standardPaths,
            result.copiedMap,
          );
          const updatedSerato = replaceExternalSeratoPaths(
            seratoTracks,
            result.copiedMap,
          );
          proceedWithExport(
            type,
            updatedPaths,
            updatedSerato,
            resolvedSeratoTarget,
          );
        };

        if (pref === 'always-copy') {
          await doCopyAndExport();
          return;
        }

        pendingExportRef.current = {
          type,
          filePaths: standardPaths,
          seratoTracks,
          seratoExportTarget: resolvedSeratoTarget,
        };
        promptForExternalDrives(
          connectedExternalPaths,
          () => doCopyAndExport(),
          () =>
            proceedWithExport(
              type,
              standardPaths,
              seratoTracks,
              resolvedSeratoTarget,
            ),
        );
      } catch (error) {
        console.error('[handleDjExport] Error:', error);
        errorToast({ message: 'Export failed due to an error' });
        setExportingType(null);
      }
    },
    [
      resolveTrackPathMap,
      getStandardExportPaths,
      getSeratoExportTracks,
      findExternalPaths,
      checkFilesExistBatch,
      promptDisconnectedDrives,
      proceedWithExport,
      pickDestinationAndCopy,
      replaceExternalPaths,
      replaceExternalSeratoPaths,
      promptForExternalDrives,
      errorToast,
    ],
  );

  // Handle PDF export
  const handlePdfExport = useCallback(async () => {
    if (trackInfo.length === 0) {
      errorToast({ message: 'No tracks to export' });
      return;
    }

    setExportingType('pdf');
    try {
      const result = await exportPdfUtil(
        crateName,
        trackInfo,
        hacker,
        cloudCrateList,
        icons,
      );
      if (result?.success) {
        successToast({
          message: isElectron()
            ? 'PDF exported successfully'
            : 'Download started',
        });
      } else if (result?.error) {
        errorToast({ message: `Failed to export PDF: ${result.error}` });
      }
    } catch (error) {
      errorToast({ message: 'Failed to export PDF' });
    }
    setExportingType(null);
  }, [
    crateName,
    trackInfo,
    hacker,
    cloudCrateList,
    icons,
    successToast,
    errorToast,
  ]);

  // Handle CSV export
  const handleCsvExport = useCallback(async () => {
    if (trackInfo.length === 0) {
      errorToast({ message: 'No tracks to export' });
      return;
    }

    setExportingType('csv');
    try {
      const result = await exportCsvUtil(
        crateName,
        trackInfo,
        hacker,
        cloudCrateList,
      );
      if (result?.success) {
        successToast({
          message: isElectron()
            ? 'CSV exported successfully'
            : 'Download started',
        });
      } else if (result?.error) {
        errorToast({ message: `Failed to export CSV: ${result.error}` });
      }
    } catch (error) {
      errorToast({ message: 'Failed to export CSV' });
    }
    setExportingType(null);
  }, [crateName, trackInfo, hacker, cloudCrateList, successToast, errorToast]);

  const handleExportUnmatchedCsv = useCallback(async () => {
    if (trackInfo.length === 0) {
      errorToast({ message: 'No tracks to check' });
      return;
    }

    setExportingType('unmatched-csv');
    const pathMap = await resolveTrackPathMap();
    const unmatchedTracks = trackInfo.filter(
      (track) => getTrackPaths(track, pathMap).length === 0,
    );

    if (unmatchedTracks.length === 0) {
      successToast({ message: 'All tracks are matched!' });
      setExportingType(null);
      return;
    }

    try {
      const result = await exportCsvUtil(
        `${crateName} (Unmatched)`,
        unmatchedTracks,
        hacker,
        cloudCrateList,
      );
      if (result?.success) {
        successToast({
          message: isElectron()
            ? `${unmatchedTracks.length} unmatched tracks exported`
            : 'Download started',
        });
      } else if (result?.error) {
        errorToast({
          message: `Failed to export unmatched tracks: ${result.error}`,
        });
      }
    } catch (error) {
      errorToast({ message: 'Failed to export unmatched tracks' });
    }
    setExportingType(null);
  }, [
    trackInfo,
    resolveTrackPathMap,
    getTrackPaths,
    crateName,
    hacker,
    cloudCrateList,
    successToast,
    errorToast,
  ]);

  const handleCopyUnmatchedToClipboard = useCallback(async () => {
    if (trackInfo.length === 0) {
      errorToast({ message: 'No tracks to copy' });
      return;
    }

    setExportingType('unmatched-copy');
    try {
      const pathMap = await resolveTrackPathMap();
      const unmatchedTracks = trackInfo.filter(
        (track) => getTrackPaths(track, pathMap).length === 0,
      );

      if (unmatchedTracks.length === 0) {
        successToast({ message: 'All tracks are matched!' });
        setExportingType(null);
        return;
      }

      const text = unmatchedTracks
        .map((t) => `${t.Artist} - ${t.Title}`)
        .join('\n');
      const success = await copyToClipboard(text);

      if (success) {
        successToast({
          message: `${unmatchedTracks.length} unmatched tracks copied`,
        });
      } else {
        errorToast({ message: 'Failed to copy to clipboard' });
      }
    } catch (error) {
      console.error('[handleCopyUnmatchedToClipboard] Error:', error);
      errorToast({ message: 'Failed to copy unmatched tracks' });
    }
    setExportingType(null);
  }, [trackInfo, resolveTrackPathMap, getTrackPaths, successToast, errorToast]);

  const handleClick = (
    type: ExportType | 'unmatched-csv' | 'unmatched-copy',
  ) => {
    if (exportingType || copyingFiles) return;

    switch (type) {
      case 'serato3':
      case 'serato4':
      case 'rekordbox':
      case 'virtualdj':
      case 'm3u':
        handleDjExport(type);
        break;
      case 'pdf':
        handlePdfExport();
        break;
      case 'csv':
        handleCsvExport();
        break;
      case 'unmatched-csv':
        handleExportUnmatchedCsv();
        break;
      case 'unmatched-copy':
        handleCopyUnmatchedToClipboard();
        break;
    }
  };

  const isElectronApp = isElectron();

  // DJ Software buttons config
  const djSoftwareButtons = [
    {
      id: 'serato3' as ExportType,
      label: 'Serato 3',
      icon: 'seratoDJPro',
      badge: '3',
      tooltip: 'Export to Serato 3',
    },
    {
      id: 'serato4' as ExportType,
      label: 'Serato 4+',
      icon: 'seratoDJPro',
      badge: '4+',
      tooltip: 'Export to Serato 4+',
    },
    { id: 'rekordbox' as ExportType, label: 'Rekordbox', icon: 'rekordbox' },
    { id: 'virtualdj' as ExportType, label: 'VirtualDJ', icon: 'virtualDJ' },
  ];

  // Other buttons config (M3U is desktop-only)
  const otherButtons = [
    ...(isElectronApp
      ? [{ id: 'm3u' as ExportType, label: 'M3U', icon: null }]
      : []),
    { id: 'pdf' as ExportType, label: 'PDF', icon: null },
    { id: 'csv' as ExportType, label: 'CSV', icon: null },
  ];

  return (
    <>
      <div className="inline-export-button">
        {/* DJ Software Group - Only show in Electron */}
        {isElectronApp && (
          <div className="export-group">
            <span className="group-label">DJ SOFTWARE</span>
            <div className="button-row">
              {djSoftwareButtons.map((btn) => (
                <div
                  key={btn.id}
                  className="export-btn-wrapper"
                  data-tooltip={btn.tooltip ?? `Export to ${btn.label}`}
                >
                  <IonButton
                    className={`export-btn ${exportingType === btn.id ? 'exporting' : ''} ${btn.badge ? 'has-serato-badge' : ''}`}
                    fill="clear"
                    size="small"
                    onClick={() => handleClick(btn.id)}
                    disabled={!!exportingType || copyingFiles}
                  >
                    {exportingType === btn.id ? (
                      <IonSpinner name="crescent" />
                    ) : btn.icon ? (
                      <>
                        <IonIcon icon={icons[btn.icon]} />
                        {btn.badge ? (
                          <span className="serato-version-badge">{btn.badge}</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-label">{btn.label}</span>
                    )}
                  </IonButton>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Other Group */}
        <div className="export-group">
          <span className="group-label">OTHER</span>
          <div className="button-row">
            {otherButtons.map((btn) => (
              <div
                key={btn.id}
                className="export-btn-wrapper"
                data-tooltip={`Export as ${btn.label}`}
              >
                <IonButton
                  className={`export-btn ${exportingType === btn.id ? 'exporting' : ''}`}
                  fill="clear"
                  size="small"
                  onClick={() => handleClick(btn.id)}
                  disabled={!!exportingType || copyingFiles}
                >
                  {exportingType === btn.id ? (
                    <IonSpinner name="crescent" />
                  ) : (
                    <span className="text-label">{btn.label}</span>
                  )}
                </IonButton>
              </div>
            ))}
          </div>
        </div>
        <div className="export-group unmatched-group">
          <span className="group-label">UNMATCHED ONLY</span>
          <div className="button-row">
            <div
              className="export-btn-wrapper"
              data-tooltip="Export Unmatched Tracks (CSV)"
            >
              <IonButton
                className={`export-btn unmatched-csv ${exportingType === 'unmatched-csv' ? 'exporting' : ''}`}
                fill="clear"
                size="small"
                onClick={() => handleClick('unmatched-csv')}
                disabled={!!exportingType || copyingFiles}
              >
                {exportingType === 'unmatched-csv' ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <>
                    <span className="text-label">CSV</span>
                  </>
                )}
              </IonButton>
            </div>
            <div
              className="export-btn-wrapper"
              data-tooltip="Copy Unmatched List"
            >
              <IonButton
                className={`export-btn unmatched-copy ${exportingType === 'unmatched-copy' ? 'exporting' : ''}`}
                fill="clear"
                size="small"
                onClick={() => handleClick('unmatched-copy')}
                disabled={!!exportingType || copyingFiles}
              >
                {exportingType === 'unmatched-copy' ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <span className="text-label">Copy</span>
                )}
              </IonButton>
            </div>
          </div>
        </div>
      </div>
      <RekordboxGuidePopover
        isOpen={showRekordBoxModal}
        onClose={() => setShowRekordBoxModal(false)}
        path={rekordBoxPath}
      />
    </>
  );
};

export default InlineExportButtons;
